import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 0,
  class: "card-top-line"
}
const _hoisted_3 = { class: "card-top-line-inner" }
const _hoisted_4 = { class: "card-name font-size-fit" }
const _hoisted_5 = { class: "card-mana-cost" }
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "card-canonical-name font-size-fit"
}
const _hoisted_8 = {
  key: 2,
  class: "card-type font-size-fit"
}
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = {
  key: 4,
  class: "card-pt font-size-fit"
}
const _hoisted_11 = {
  key: 5,
  class: "card-loyalty font-size-fit"
}

import { ScryfallCard, isScryfallCard, ScryfallCardFace, CardCacheEntry, isScryfallCardFace } from "../vueCardCache";
import { ref, computed, watch, onMounted, nextTick, onUnmounted } from "vue";
import { replaceManaSymbols, parseManaSymbols } from "../ManaSymbols";
import { Card, CardFace } from "@/CardTypes";
import { fitFontSize } from "../helper";

// Displays a card using Scryfall card data instead of its image.

export default /*@__PURE__*/_defineComponent({
  __name: 'CardText',
  props: {
    card: {},
    fixedLayout: { type: Boolean, default: false },
    delayTextFit: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const rootElement = ref<HTMLElement>();

let idleRequest: number | undefined = undefined;

onMounted(() => {
	// This has to be called when the component is visible:
	// We can't use v-show or mounted will be called while the element is hidden and fitAll will do nothing.
	// There's no way to know when the element is visible becasue of v-show (apart from tracking it ourselves).
	// NOTE: Fitting a lot of cards simultaneously is very slow in Chrome (e.g. for a cube list). delayTextFit is a hack to stagger these calls and not block the main thread.
	if (props.delayTextFit && window.requestIdleCallback) idleRequest = window.requestIdleCallback(fitAll);
	else fitAll();
});

onUnmounted(() => {
	if (idleRequest) window.cancelIdleCallback(idleRequest);
});

function fitAll() {
	nextTick(() => {
		rootElement.value?.querySelectorAll(".card-text .font-size-fit").forEach((div) => {
			fitFontSize(div as HTMLElement, 2.5, "vh");
		});
	});
}

function parseOracle(str: string) {
	str = replaceManaSymbols(str);
	// Included reminder text
	str = str.replace(/\([^)]+\)/g, (match) => `<span class="oracle-reminder">${match}</span>`);
	return str
		.split("\n")
		.map((line) => `<div>${line}</div>`)
		.join("");
}

const manaCost = computed(() => {
	if (!face.value?.mana_cost) return [];
	return parseManaSymbols(face.value.mana_cost);
});

const colors = computed(() => {
	if (!props.card) return undefined;
	if (isScryfallCard(props.card)) {
		return props.card.colors;
	} else if (isScryfallCardFace(props.card)) {
		return props.card.colors;
	}
	if ("colors" in props.card) return props.card.colors;
	return undefined;
});

const backColor = computed(() => {
	if (!colors.value) return undefined;
	if (colors.value?.length === 1) return colors.value[0];
	return "M";
});

const face = computed(() => {
	if (!props.card) return undefined;
	if (isScryfallCard(props.card)) {
		if (!props.card?.card_faces || props.card?.card_faces?.length <= 1) return props.card;
		return props.card.card_faces[0];
	} else if (isScryfallCardFace(props.card)) {
		return props.card;
	}
	const r: Partial<ScryfallCardFace> = {};
	if (
		"printed_names" in props.card &&
		props.card.printed_names?.["en"] &&
		props.card.printed_names["en"] !== props.card.name
	) {
		r.printed_name = props.card.printed_names["en"];
	}
	if ("name" in props.card) r.name = props.card.name;
	if ("mana_cost" in props.card) r.mana_cost = props.card.mana_cost;
	if ("type" in props.card) {
		r.type_line = props.card.type;
		if ("subtypes" in props.card && props.card.subtypes.length > 0)
			r.type_line += " \u2013 " + props.card.subtypes.join(" ");
	}
	if ("oracle_text" in props.card) r.oracle_text = props.card.oracle_text;
	if ("power" in props.card) r.power = props.card.power?.toString();
	if ("toughness" in props.card) r.toughness = props.card.toughness?.toString();
	if ("loyalty" in props.card) r.loyalty = props.card.loyalty?.toString();
	return r;
});

const printed_name = computed(() => {
	if (!face.value) return undefined;
	if ("printed_name" in face.value) return face.value.printed_name;
	return face.value.name;
});

const canonical_name = computed(() => {
	if (!face.value) return undefined;
	if ("printed_name" in face.value) return face.value.name;
	return undefined;
});

watch(face, () => {
	fitAll();
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: "card-text-container",
    ref_key: "rootElement",
    ref: rootElement
  }, [
    (!face.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_component_font_awesome_icon, {
            icon: "fa-solid fa-spinner",
            spin: ""
          })
        ]))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["card-text", 'color-' + backColor.value])
        }, [
          (printed_name.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, _toDisplayString(printed_name.value), 1 /* TEXT */),
                  _createElementVNode("div", _hoisted_5, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(manaCost.value, (s, idx) => {
                      return (_openBlock(), _createElementBlock("img", {
                        key: idx,
                        src: s.svg_uri,
                        class: "mana-symbol"
                      }, null, 8 /* PROPS */, _hoisted_6))
                    }), 128 /* KEYED_FRAGMENT */))
                  ])
                ])
              ]))
            : _createCommentVNode("v-if", true),
          (canonical_name.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(canonical_name.value), 1 /* TEXT */))
            : _createCommentVNode("v-if", true),
          (face.value.type_line)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(face.value.type_line), 1 /* TEXT */))
            : _createCommentVNode("v-if", true),
          (face.value.oracle_text)
            ? (_openBlock(), _createElementBlock("div", {
                key: 3,
                class: "card-oracle font-size-fit",
                innerHTML: parseOracle(face.value.oracle_text)
              }, null, 8 /* PROPS */, _hoisted_9))
            : _createCommentVNode("v-if", true),
          (face.value.power)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(face.value.power) + " / " + _toDisplayString(face.value.toughness), 1 /* TEXT */))
            : _createCommentVNode("v-if", true),
          (face.value.loyalty)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(face.value.loyalty), 1 /* TEXT */))
            : _createCommentVNode("v-if", true)
        ], 2 /* CLASS */))
  ], 512 /* NEED_PATCH */))
}
}

})